<template>
  <div>
    <page-header
      icon="mdi-account-clock-outline"
      :title="$t('guest_report')"
      :subtitle="$t('manage_guest_orders_reports')"
    >
      <template #actions v-if="!device.isMobile">
        <primary-button class="mr-2" @click="openKitchenDisplay">
          <v-icon class="mr-2">mdi-chef-hat</v-icon>
          {{ $t("kitchen_display") }}
        </primary-button>
        <excel-button @click="downloadExcelReport" class="mr-2" />
        <print-button @click="printReport" />
      </template>
    </page-header>

    <!-- Desktop Filters -->
    <page-toolbar
      v-if="!device.isMobile"
      :search.sync="searchQuery"
      :search-placeholder="$t('search')"
      searchable
    >
      <template #filters>
        <!-- Show restaurant selector for admin users -->
        <v-col cols="12" sm="6" lg="auto" v-if="user && user.type === 'admin'">
          <autocomplete
            v-model="selectedRestaurants"
            :items="availableRestaurants"
            :label="$t('select_restaurant')"
            return-object
            item-text="name"
            select-all
            item-value="id"
            color="white"
            hide-details
            clearable
            multiple
            chips
            small-chips
            deletable-chips
          />
        </v-col>

        <!-- Show single date picker based on current tab filter config -->
        <v-col
          cols="12"
          sm="6"
          lg="auto"
          v-if="currentTabConfig && currentTabConfig.filters.singleDate"
        >
          <date-picker v-model="selectedDate" single-date color="white" />
        </v-col>

        <!-- Show date range picker based on current tab filter config -->
        <v-col
          cols="12"
          sm="6"
          lg="auto"
          v-if="currentTabConfig && currentTabConfig.filters.dateRange"
        >
          <date-picker v-model="dateRange" color="white" />
        </v-col>

        <!-- Show status filter based on current tab filter config -->
        <v-col
          cols="12"
          sm="6"
          lg="auto"
          v-if="currentTabConfig && currentTabConfig.filters.status"
        >
          <autocomplete
            v-model="selectedStatus"
            :items="statusOptions"
            :label="$t('status')"
            return-object
            item-text="name"
            item-value="id"
            color="white"
            hide-details
          />
        </v-col>

        <!-- Show delivery type filter based on current tab filter config -->
        <v-col
          cols="12"
          sm="6"
          lg="auto"
          v-if="currentTabConfig && currentTabConfig.filters.deliveryType"
        >
          <autocomplete
            v-model="selectedDeliveryType"
            :items="deliveryTypeOptions"
            :label="$t('delivery_location')"
            return-object
            item-text="name"
            item-value="id"
            color="white"
            hide-details
          />
        </v-col>
      </template>
    </page-toolbar>

    <!-- Mobile Filters -->
    <mobile-filters
      v-if="device.isMobile"
      :filters="mobileFilterConfig"
      @update:filter="handleFilterUpdate"
      @apply="onFiltersApply"
      @reset="resetFilters"
    >
      <template v-slot:actions>
        <excel-button @click="downloadExcelReport" class="mr-2" size="small" />
        <print-button @click="printReport" size="small" />
      </template>
    </mobile-filters>

    <div
      :class="{
        'px-6': device.isDesktop,
      }"
    >
      <v-tabs v-model="currentTab" background-color="primary" dark>
        <v-tab v-for="tab in availableTabs" :key="tab.id">
          <v-icon class="mr-2">{{ tab.icon }}</v-icon>
          {{ $t(tab.label) }}
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="currentTab" touchless>
        <v-tab-item v-for="tab in availableTabs" :key="tab.id">
          <component
            v-if="tab.component"
            :is="tab.component"
            :search-query="searchQuery"
            :selected-date="selectedDate"
            :date-range="dateRange"
            :selected-status="selectedStatus"
            :selected-delivery-type="selectedDeliveryType"
            :selected-restaurants="selectedRestaurants"
            :restaurant-ids="restaurantIds"
            :is-active-tab="currentTab === availableTabs.indexOf(tab)"
          />
          <v-card v-else flat>
            <v-card-text>{{ $t(tab.label) }} - Coming soon</v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </div>
  </div>
</template>

<script>
import OrdersTab from "./tabs/OrdersTab.vue";
import ReportTab from "./tabs/ReportTab.vue";
import RestaurantsTab from "./tabs/RestaurantsTab.vue";
import NamirniceTab from "./tabs/NamirniceTab.vue";
import MealsTab from "./tabs/MealsTab.vue";
import ObrociTab from "./tabs/ObrociTab.vue";
import TakenTab from "./tabs/TakenTab.vue";
import KorisniciTab from "./tabs/KorisniciTab.vue";
import IncomeTab from "./tabs/IncomeTab.vue";
import CompaniesDeliveryTab from "./tabs/CompaniesDeliveryTab.vue";
// Admin tabs
import AdminReportTab from "./tabs/admin/AdminReportTab.vue";
import AdminMealsTab from "./tabs/admin/AdminMealsTab.vue";
import AdminObrociTab from "./tabs/admin/AdminObrociTab.vue";
import AdminTakenTab from "./tabs/admin/AdminTakenTab.vue";
import AdminKorisniciTab from "./tabs/admin/AdminKorisniciTab.vue";
import AdminIncomeTab from "./tabs/admin/AdminIncomeTab.vue";
import AdminNamirniceTab from "./tabs/admin/AdminNamirniceTab.vue";
import AdminCompaniesDeliveryTab from "./tabs/admin/AdminCompaniesDeliveryTab.vue";
import AdminRestaurantsTab from "./tabs/admin/AdminRestaurantsTab.vue";
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";
import MobileFilters from "@/components/global/MobileFilters.vue";

export default {
  name: "GuestReport",
  mixins: [hasPrinting, hasExcel],
  components: {
    OrdersTab,
    ReportTab,
    RestaurantsTab,
    NamirniceTab,
    MealsTab,
    ObrociTab,
    TakenTab,
    KorisniciTab,
    IncomeTab,
    CompaniesDeliveryTab,
    AdminReportTab,
    AdminMealsTab,
    AdminObrociTab,
    AdminTakenTab,
    AdminKorisniciTab,
    AdminIncomeTab,
    AdminNamirniceTab,
    AdminCompaniesDeliveryTab,
    AdminRestaurantsTab,
    MobileFilters,
  },
  data() {
    return {
      searchQuery: "",
      selectedDate: new Date().toISOString().substr(0, 10),
      dateRange: [
        new Date().toISOString().substr(0, 10),
        new Date().toISOString().substr(0, 10),
      ],
      selectedStatus: { id: 0, name: "all" },
      selectedDeliveryType: { id: 3, name: "all" },
      selectedRestaurants: [],
      availableRestaurants: [],
      currentTab: 0,
      deliveryTypeOptions: [
        { id: 3, name: this.$t("all") },
        { id: 0, name: this.$t("home_delivery") },
        { id: 1, name: this.$t("pickup_at_restaurant") },
        { id: 2, name: this.$t("dine_in_restaurant") },
        { id: 5, name: this.$t("express_mail") },
      ],
      restaurantTabs: [
        {
          id: "guests",
          label: "guests",
          icon: "mdi-cart",
          component: "report-tab",
          filters: { dateRange: true, status: true, deliveryType: true },
        },
        {
          id: "namirnice",
          label: "namirnice",
          icon: "mdi-food-apple",
          component: "namirnice-tab",
          filters: { dateRange: true },
        },
        {
          id: "meals",
          label: "routes.jela",
          icon: "mdi-food",
          component: "meals-tab",
          filters: { dateRange: true },
        },
        {
          id: "obroci",
          label: "meals",
          icon: "mdi-silverware-fork-knife",
          component: "obroci-tab",
          filters: { dateRange: true },
        },
        {
          id: "taken",
          label: "taken",
          icon: "mdi-cart-check",
          component: "taken-tab",
          filters: { dateRange: true },
        },
        {
          id: "korisnici",
          label: "korisnici",
          icon: "mdi-account-group",
          component: "korisnici-tab",
          filters: { dateRange: true },
        },
        {
          id: "income",
          label: "income",
          icon: "mdi-cash-multiple",
          component: "income-tab",
          filters: { dateRange: true },
        },
        {
          id: "companies_delivery",
          label: "companies_delivery",
          icon: "mdi-truck-delivery",
          component: "companies-delivery-tab",
          filters: { dateRange: true, status: true },
        },
        {
          id: "restaurants",
          label: "restaurants",
          icon: "mdi-silverware",
          component: "restaurants-tab",
          filters: { dateRange: true, status: true, deliveryType: true },
        },
      ],
      adminTabs: [
        {
          id: "guests",
          label: "guests",
          icon: "mdi-cart",
          component: "admin-report-tab",
          filters: { dateRange: true, status: true, deliveryType: true },
        },
        {
          id: "namirnice",
          label: "namirnice",
          icon: "mdi-food-apple",
          component: "admin-namirnice-tab",
          filters: { dateRange: true },
        },
        {
          id: "meals",
          label: "routes.jela",
          icon: "mdi-food",
          component: "admin-meals-tab",
          filters: { dateRange: true },
        },
        {
          id: "obroci",
          label: "meals",
          icon: "mdi-silverware-fork-knife",
          component: "admin-obroci-tab",
          filters: { dateRange: true },
        },
        {
          id: "taken",
          label: "taken",
          icon: "mdi-cart-check",
          component: "admin-taken-tab",
          filters: { dateRange: true },
        },
        {
          id: "korisnici",
          label: "korisnici",
          icon: "mdi-account-group",
          component: "admin-korisnici-tab",
          filters: { dateRange: true },
        },
        {
          id: "income",
          label: "income",
          icon: "mdi-cash-multiple",
          component: "admin-income-tab",
          filters: { dateRange: true },
        },
        {
          id: "companies_delivery",
          label: "companies_delivery",
          icon: "mdi-truck-delivery",
          component: "admin-companies-delivery-tab",
          filters: { dateRange: true, status: true },
        },
        {
          id: "restaurants",
          label: "restaurants",
          icon: "mdi-silverware",
          component: "admin-restaurants-tab",
          filters: { dateRange: true, status: true, deliveryType: true },
        },
      ],
      guestTabs: [
        {
          id: "orders",
          label: "orders",
          icon: "mdi-cart",
          component: "orders-tab",
          filters: { singleDate: true, status: true, deliveryType: true },
        },
        {
          id: "report",
          label: "report",
          icon: "mdi-chart-box",
          component: "report-tab",
          filters: { dateRange: true, status: true, deliveryType: true },
        },
        {
          id: "restaurants",
          label: "restaurants",
          icon: "mdi-silverware-fork-knife",
          component: "restaurants-tab",
          filters: { singleDate: true, status: true, deliveryType: true },
        },
      ],
    };
  },
  computed: {
    user() {
      return this.$store.state.user.user;
    },
    availableTabs() {
      if (this.user?.type === "restaurant") {
        return this.restaurantTabs;
      } else if (this.user?.type === "admin") {
        return this.adminTabs;
      } else {
        return this.guestTabs;
      }
    },
    currentTabConfig() {
      return this.availableTabs[this.currentTab] || null;
    },
    restaurantIds() {
      if (this.user?.type === "admin" && this.selectedRestaurants.length > 0) {
        return this.selectedRestaurants.map((r) => r.id).join(",");
      }
      return "";
    },
    statusOptions() {
      return this.currentTab === 2
        ? [
            { id: 0, name: this.$t("all") },
            { id: 1, name: this.$t("ordered_meal1"), alias: "ordered_meal1" },
            {
              id: 2,
              name: this.$t("prepared_for_delivery1"),
              alias: "prepared_for_delivery1",
            },
            { id: 4, name: this.$t("ready"), alias: "ready" },
            { id: 6, name: this.$t("delivered_11"), alias: "delivered_11" },
            { id: 10, name: this.$t("payed"), alias: "payed" },
            {
              id: 7,
              name: this.$t("declined_by_restoran1"),
              alias: "declined_by_restoran1",
            },
            { id: 99, name: this.$t("undefined"), alias: "undefined" },
          ]
        : [
            { id: 0, name: this.$t("all") },
            { id: 1, name: this.$t("ordered_meal1") },
            { id: 2, name: this.$t("prepared_for_delivery1") },
            { id: 3, name: this.$t("booked_by_delivery") },
            { id: 4, name: this.$t("ready") },
            { id: 5, name: this.$t("taken_by_delivery1") },
            { id: 6, name: this.$t("delivered_11") },
            { id: 10, name: this.$t("payed") },
            { id: 7, name: this.$t("declined_by_restoran1") },
            { id: 8, name: this.$t("delivery_cancel") },
            { id: 9, name: this.$t("order_not_taken1") },
          ];
    },
    mobileFilterConfig() {
      const config = {};

      // Add restaurant selector for admin users
      if (this.user && this.user.type === "admin") {
        config.selectedRestaurants = {
          type: "autocomplete",
          label: this.$t("select_restaurant"),
          icon: "mdi-silverware",
          items: this.availableRestaurants,
          value: this.selectedRestaurants,
          returnObject: true,
          itemText: "name",
          itemValue: "id",
          multiple: true,
          chips: true,
          smallChips: true,
          chipText:
            this.selectedRestaurants.length > 0
              ? `${this.selectedRestaurants.length} ${this.$t("selected")}`
              : this.$t("select_restaurant"),
          visible: this.selectedRestaurants.length > 0,
          clearable: true,
        };
      }

      // Add filters based on current tab configuration
      if (this.currentTabConfig) {
        if (this.currentTabConfig.filters.singleDate) {
          config.selectedDate = {
            type: "date",
            label: this.$t("date"),
            icon: "mdi-calendar",
            value: [this.selectedDate, this.selectedDate],
            chipText: this.formatDateChip(this.selectedDate) || this.$t("date"),
            visible: !!this.selectedDate,
            clearable: true,
          };
        }

        if (this.currentTabConfig.filters.dateRange) {
          config.dateRange = {
            type: "date",
            label: this.$t("date"),
            icon: "mdi-calendar",
            value: this.dateRange,
            chipText: this.formatDateRangeChip || this.$t("date"),
            visible: this.dateRange && this.dateRange.length === 2,
            clearable: true,
          };
        }

        if (this.currentTabConfig.filters.status) {
          config.selectedStatus = {
            type: "autocomplete",
            label: this.$t("status"),
            icon: "mdi-information",
            items: this.statusOptions,
            value: this.selectedStatus,
            returnObject: true,
            itemText: "name",
            itemValue: "id",
            chipText: this.selectedStatus?.name || this.$t("status"),
            visible: this.selectedStatus && this.selectedStatus.id !== 0,
            clearable: true,
          };
        }

        if (this.currentTabConfig.filters.deliveryType) {
          config.selectedDeliveryType = {
            type: "autocomplete",
            label: this.$t("delivery_location"),
            icon: "mdi-map-marker",
            items: this.deliveryTypeOptions,
            value: this.selectedDeliveryType,
            returnObject: true,
            itemText: "name",
            itemValue: "id",
            chipText: this.selectedDeliveryType?.name || this.$t("delivery_location"),
            visible: this.selectedDeliveryType && this.selectedDeliveryType.id !== 3,
            clearable: true,
          };
        }
      }

      return config;
    },
    formatDateRangeChip() {
      if (!this.dateRange || !this.dateRange[0]) return "";
      const formatDate = (date) => {
        const d = new Date(date);
        return `${d.getDate().toString().padStart(2, "0")}.${(d.getMonth() + 1)
          .toString()
          .padStart(2, "0")}.`;
      };
      if (this.dateRange[0] === this.dateRange[1] || !this.dateRange[1]) {
        return formatDate(this.dateRange[0]);
      }
      return `${formatDate(this.dateRange[0])} - ${formatDate(this.dateRange[1])}`;
    },
  },
  mounted() {
    if (this.user && this.user.type === "admin" && this.user.restaurants) {
      this.user.restaurants.filter((item) => {
        if (item.settings && item.settings.lice_bez_ugovora) {
          this.availableRestaurants.push(item);
        }
      });
      // Pre-select all restaurants
      this.selectedRestaurants = [...this.availableRestaurants];
    }
  },
  methods: {
    printReport() {
      this.setPrinter();
    },
    downloadExcelReport() {
      this.setExcel();
    },
    openKitchenDisplay() {
      this.$router.push({ name: "kitchen-display" });
    },
    handleFilterUpdate({ key, value }) {
      if (key === "selectedDate") {
        this.selectedDate = value[0] || new Date().toISOString().substr(0, 10);
      } else {
        this[key] = value;
      }
    },
    onFiltersApply() {
      // Filters are already reactive, no additional action needed
    },
    resetFilters() {
      this.searchQuery = "";
      this.selectedDate = new Date().toISOString().substr(0, 10);
      this.dateRange = [
        new Date().toISOString().substr(0, 10),
        new Date().toISOString().substr(0, 10),
      ];
      this.selectedStatus = { id: 0, name: "all" };
      this.selectedDeliveryType = { id: 3, name: "all" };
      this.selectedRestaurants = [];
    },
    formatDateChip(date) {
      if (!date) return "";
      const d = new Date(date);
      return `${d.getDate().toString().padStart(2, "0")}.${(d.getMonth() + 1)
        .toString()
        .padStart(2, "0")}.`;
    },
  },
};
</script>
